import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { interventiApi, reportApi, spostaApi } from '../services/api';
import { useAuth } from '../context/AuthContext';

const STATI = ['tutti', 'programmato', 'in_corso', 'completato', 'annullato'];

const BADGE = {
  programmato: 'badge-blue',
  in_corso:    'badge-orange',
  completato:  'badge-green',
  annullato:   'badge-gray',
};

export default function InterventiPage() {
  const [interventi, setInterventi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [stato, setStato] = useState('tutti');
  const [vistaZona, setVistaZona] = useState(false);
  const [reportLoad, setReportLoad] = useState(null);
  const { isAdmin } = useAuth();
  const [sposta, setSposta] = useState(null);   // { intervento, data, cascata, prev, loading }
  const [refresh, setRefresh] = useState(0);
  const [tecnici, setTecnici] = useState([]);
  const [assegnando, setAssegnando] = useState(null);

  useEffect(() => {
    api.get('/interventi/tecnici')
      .then(r => setTecnici(r.data?.data || []))
      .catch(() => {});
  }, []);

  // Prossimi 4 mesi a partire da quello corrente
  const mesiReport = Array.from({ length: 4 }, (_, k) => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() + k);
    return {
      anno: d.getFullYear(),
      mese: d.getMonth() + 1,
      label: d.toLocaleDateString('it-IT', { month: 'short', year: '2-digit' }),
    };
  });

  const scaricaReport = async (anno, mese) => {
    setReportLoad(`${anno}-${mese}`);
    try {
      const res = await reportApi.interventi(anno, mese);
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Interventi_${anno}_${String(mese).padStart(2, '0')}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      let msg = 'Errore generazione report';
      try { msg = JSON.parse(await e.response.data.text()).error || msg; } catch {}
      alert(msg);
    } finally {
      setReportLoad(null);
    }
  };
  const navigate = useNavigate();

  useEffect(() => {
    interventiApi.getAll()
      .then(res => setInterventi(res.data?.data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [refresh]);

  const fmtData = d => d ? new Date(d).toLocaleDateString('it-IT') : '—';

  const filtrati = interventi.filter(i => {
    const matchQ = !q || i.sitoNome?.toLowerCase().includes(q.toLowerCase()) ||
                   i.sito_nome?.toLowerCase().includes(q.toLowerCase()) ||
                   i.indirizzo?.toLowerCase().includes(q.toLowerCase());
    const matchS = stato === 'tutti' || i.stato === stato;
    return matchQ && matchS;
  }).sort((a, b) => {
    // Ordina per data intervento crescente (prima i più vicini)
    const da = new Date(a.dataIntervento || a.data_intervento || 0);
    const db = new Date(b.dataIntervento || b.data_intervento || 0);
    return da - db;
  });

  // Raggruppa per zona
  const perZona = filtrati.reduce((acc, i) => {
    const z = i.zona || 'Senza zona';
    if (!acc[z]) acc[z] = [];
    acc[z].push(i);
    return acc;
  }, {});

  if (loading) return <div className="loading"><div className="spinner"></div></div>;

  const apriSposta = (i) => {
    const d = new Date(i.dataIntervento || i.data_intervento);
    setSposta({ intervento: i, data: d.toISOString().slice(0, 10),
                cascata: false, prev: null, loading: false });
  };

  const anteprima = async (data, cascata) => {
    setSposta(s => ({ ...s, data, cascata, loading: true }));
    try {
      const res = await spostaApi.preview(sposta.intervento.id, data, cascata);
      setSposta(s => ({ ...s, prev: res.data, loading: false }));
    } catch (e) {
      setSposta(s => ({ ...s, prev: null, loading: false }));
      alert(e.response?.data?.error || 'Errore anteprima');
    }
  };

  const confermaSposta = async () => {
    setSposta(s => ({ ...s, loading: true }));
    try {
      const res = await spostaApi.conferma(sposta.intervento.id, sposta.data, sposta.cascata);
      setSposta(null);
      alert(`${res.data.spostati} intervento/i aggiornato/i.`);
      setRefresh(r => r + 1);
    } catch (e) {
      setSposta(s => ({ ...s, loading: false }));
      alert(e.response?.data?.error || 'Errore spostamento');
    }
  };

  const assegna = async (interventoId, tecnicoId) => {
    setAssegnando(interventoId);
    try {
      await api.put(`/interventi/${interventoId}/tecnico`, { tecnicoId: tecnicoId ? Number(tecnicoId) : null });
      setRefresh(r => r + 1);
    } catch (e) {
      alert(e.response?.data?.error || 'Errore assegnazione');
    } finally { setAssegnando(null); }
  };

  const RigaIntervento = ({ i }) => (
    <tr style={{ cursor: 'pointer' }} onClick={() => { if (String(i.id) !== 'nuovo') navigate(`/interventi/${i.id}`); }}>
      <td><strong>{i.sitoNome || i.sito_nome}</strong></td>
      <td style={{ fontSize: 12, color: '#6b7280' }}>{i.indirizzo || '—'}</td>
      <td>{fmtData(i.dataIntervento || i.data_intervento)}</td>
      <td>{i.zona && <span className="badge badge-gray">{i.zona}</span>}</td>
      <td><span className={`badge ${BADGE[i.stato] || 'badge-gray'}`}>{i.stato}</span></td>
      <td onClick={e => e.stopPropagation()}>
        {isAdmin ? (
          <select value={i.tecnico_id || i.tecnicoId || ''}
            disabled={assegnando === i.id}
            onChange={e => assegna(i.id, e.target.value)}
            style={{ fontSize: 12, padding: '2px 4px', maxWidth: 130 }}>
            <option value="">— non assegnato —</option>
            {tecnici.map(t => (
              <option key={t.id} value={t.id}>{t.nome || t.username}</option>
            ))}
          </select>
        ) : (
          <span style={{ fontSize: 12, color: '#6b7280' }}>
            {tecnici.find(t => t.id === (i.tecnico_id || i.tecnicoId))?.username || '—'}
          </span>
        )}
      </td>
      <td>
        {isAdmin && i.stato === 'programmato' && (
          <button className="btn btn-secondary btn-sm" title="Sposta data"
            style={{ marginRight: 6 }}
            onClick={e => { e.stopPropagation(); apriSposta(i); }}>
            📅
          </button>
        )}
        <button className="btn btn-secondary btn-sm"
          onClick={e => { e.stopPropagation(); navigate(`/interventi/${i.id}`); }}>
          Apri →
        </button>
      </td>
    </tr>
  );

  return (
    <div className="page-content">
      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <input className="form-control" style={{ maxWidth: 260 }}
          placeholder="🔍 Cerca intervento..."
          value={q} onChange={e => setQ(e.target.value)} />
        <select className="form-control" style={{ maxWidth: 160 }}
          value={stato} onChange={e => setStato(e.target.value)}>
          {STATI.map(s => <option key={s} value={s}>{s === 'tutti' ? 'Tutti' : s}</option>)}
        </select>
        <button
          className={`btn ${vistaZona ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setVistaZona(v => !v)}>
          {vistaZona ? '📋 Lista' : '🗺️ Per zona'}
        </button>
        <button className="btn btn-primary" onClick={() => navigate('/interventi/nuovo')}>
          ➕ Nuovo
        </button>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center', borderLeft: '1px solid #e5e7eb', paddingLeft: 12 }}>
          <span style={{ fontSize: 12, color: '#6b7280' }}>📄 Report</span>
          {mesiReport.map(m => (
            <button key={`${m.anno}-${m.mese}`}
              className="btn btn-secondary"
              style={{ padding: '4px 8px', fontSize: 12, textTransform: 'capitalize' }}
              disabled={reportLoad !== null}
              onClick={() => scaricaReport(m.anno, m.mese)}>
              {reportLoad === `${m.anno}-${m.mese}` ? '⏳' : m.label}
            </button>
          ))}
        </div>
        <span style={{ marginLeft: 'auto', alignSelf: 'center', color: '#9ca3af', fontSize: 13 }}>
          {filtrati.length} interventi
        </span>
      </div>

      {sposta && (
        <div onClick={() => !sposta.loading && setSposta(null)}
          style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.45)', zIndex:1000,
                   display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background:'#fff', borderRadius:10, padding:20, width:'100%', maxWidth:520,
                     maxHeight:'85vh', overflowY:'auto' }}>
            <h3 style={{ margin:'0 0 4px', fontSize:16 }}>📅 Sposta intervento</h3>
            <div style={{ fontSize:13, color:'#6b7280', marginBottom:16 }}>
              {sposta.intervento.sitoNome || sposta.intervento.sito_nome}
              {' — attualmente '}{fmtData(sposta.intervento.dataIntervento || sposta.intervento.data_intervento)}
            </div>

            <label style={{ fontSize:13, fontWeight:600, display:'block', marginBottom:4 }}>Nuova data</label>
            <input type="date" className="form-control" value={sposta.data}
              onChange={e => anteprima(e.target.value, sposta.cascata)} />

            <label style={{ display:'flex', alignItems:'center', gap:8, margin:'14px 0', fontSize:13 }}>
              <input type="checkbox" checked={sposta.cascata}
                onChange={e => anteprima(sposta.data, e.target.checked)} />
              Sposta anche le visite successive di questo cliente
            </label>

            {sposta.loading && <div style={{ fontSize:13, color:'#6b7280' }}>Calcolo in corso…</div>}

            {sposta.prev && !sposta.loading && (
              <div style={{ background:'#f9fafb', borderRadius:8, padding:12, fontSize:13 }}>
                {sposta.prev.avviso && (
                  <div style={{ color:'#b45309', marginBottom:8 }}>⚠️ {sposta.prev.avviso}</div>
                )}
                {sposta.prev.successivi.length > 0 ? (
                  <>
                    <div style={{ fontWeight:600, marginBottom:6 }}>
                      Verranno spostati anche {sposta.prev.successivi.length} interventi:
                    </div>
                    {sposta.prev.successivi.map(s => (
                      <div key={s.id} style={{ display:'flex', gap:8, padding:'2px 0' }}>
                        <span style={{ color:'#6b7280' }}>{s.da}</span>
                        <span>→</span>
                        <strong>{s.a}</strong>
                      </div>
                    ))}
                  </>
                ) : (
                  <div style={{ color:'#6b7280' }}>Solo questo intervento verrà modificato.</div>
                )}
              </div>
            )}

            <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:18 }}>
              <button className="btn btn-secondary" disabled={sposta.loading}
                onClick={() => setSposta(null)}>Annulla</button>
              <button className="btn btn-primary" disabled={sposta.loading}
                onClick={confermaSposta}>Conferma</button>
            </div>
          </div>
        </div>
      )}

      {vistaZona ? (
        // Vista per zona
        Object.entries(perZona)
          .sort(([a], [b]) => a === 'Senza zona' ? 1 : b === 'Senza zona' ? -1 : a.localeCompare(b))
          .map(([zona, items]) => (
            <div key={zona} style={{ marginBottom: 16 }}>
              <div style={{
                background: 'var(--red)', color: '#fff',
                padding: '8px 16px', borderRadius: '8px 8px 0 0',
                fontWeight: 600, fontSize: 14,
                display: 'flex', justifyContent: 'space-between'
              }}>
                <span>📍 {zona}</span>
                <span style={{ opacity: .8, fontWeight: 400, fontSize: 12 }}>{items.length} interventi</span>
              </div>
              <div className="card" style={{ padding: 0, borderRadius: '0 0 8px 8px', borderTop: 'none' }}>
                <div className="table-wrap">
                  <table className="q-table">
                    <thead><tr><th>Cliente</th><th>Indirizzo</th><th>Data</th><th>Zona</th><th>Stato</th><th>Tecnico</th><th></th></tr></thead>
                    <tbody>{items.map(i => <RigaIntervento key={i.id} i={i} />)}</tbody>
                  </table>
                </div>
              </div>
            </div>
          ))
      ) : (
        // Vista lista
        <div className="card" style={{ padding: 0 }}>
          <div className="table-wrap">
            <table className="q-table">
              <thead><tr><th>Cliente</th><th>Indirizzo</th><th>Data</th><th>Zona</th><th>Stato</th><th>Tecnico</th><th></th></tr></thead>
              <tbody>
                {filtrati.length === 0 && (
                  <tr><td colSpan={7} style={{ textAlign: 'center', color: '#9ca3af', padding: 32 }}>
                    Nessun intervento trovato
                  </td></tr>
                )}
                {filtrati.map(i => <RigaIntervento key={i.id} i={i} />)}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
